// quiz.js — analytics + UX for the OneVit quiz flow

import { initTabs } from './components/tab';
import { initSelectDropdowns } from './components/selectdropdown';
import { initProductPurchaseInstance } from './utils/productpurchase';
import { capture } from './analytics/posthog';

// ─── Analytics helpers ────────────────────────────────────────────────────────

function pushEvent(eventName, params = {}) {
  // GA4 via dataLayer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: eventName, ...params });

  // Meta Pixel
  if (typeof fbq === 'function') {
    fbq('trackCustom', eventName, params);
  }

  // PostHog gets the funnel steps but never the answers: they're health information.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { answer, ...safeParams } = params as Record<string, unknown>;
  capture(eventName, safeParams);
}

// ─── Thinking screen auto-advance ─────────────────────────────────────────────

function initThinkingRedirect() {
  const el = document.querySelector('[data-thinking-redirect]');
  if (!el) return;

  const nextUrl = el.dataset.thinkingRedirect;
  const delay = parseInt(el.dataset.redirectDelay ?? '3000', 10);
  const bar = el.querySelector('.quiz-thinking-bar');

  // Animate the progress bar over the redirect delay
  if (bar) {
    bar.style.transition = `width ${delay / 1000}s linear`;
    // Double-rAF ensures the browser commits the initial state before the
    // transition starts — avoids a forced synchronous reflow.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        (bar as HTMLElement).style.width = '100%';
      });
    });
  }

  setTimeout(() => {
    window.htmx.ajax('GET', nextUrl, {
      target: '#quiz-page-content',
      select: '#quiz-page-content',
      swap: 'outerHTML',
      pushUrl: true,
    });

    document.body.addEventListener(
      'htmx:afterSettle',
      function onAfterSettle() {
        document.body.removeEventListener('htmx:afterSettle', onAfterSettle);
        reattachDrawerTriggers();
      },
    );
  }, delay);
}

function reattachDrawerTriggers() {
  const triggers = document.querySelectorAll(
    '[data-drawer-trigger]:not([data-drawer-init])',
  );
  triggers.forEach((trigger) => {
    trigger.setAttribute('data-drawer-init', '');
    const drawerName = trigger.getAttribute('data-drawer-trigger');
    const drawerWrapper = document.querySelector(
      `[data-drawer-wrapper="${drawerName}"]`,
    );
    if (!drawerWrapper) return;
    const drawer = drawerWrapper.querySelector('[data-drawer]');
    const backgroundBlur = drawerWrapper.querySelector(
      '[data-drawer-background]',
    );
    const timeout = Number(trigger.getAttribute('data-drawer-timeout')) || 0;
    trigger.addEventListener('click', () => {
      setTimeout(() => {
        drawerWrapper.dataset.state = 'open';
        if (drawer) drawer.dataset.state = 'open';
        if (backgroundBlur) backgroundBlur.dataset.state = 'open';
        document.body.classList.add('overflow-hidden');
      }, timeout);
    });
  });
}

// ─── Radio / checkbox option highlight ───────────────────────────────────────

function initOptionHighlight() {
  // Radio options
  document
    .querySelectorAll('[data-quiz-input-type="radio"] .quiz-option')
    .forEach((label) => {
      const input = label.querySelector('input[type="radio"]');
      if (!input) return;

      label.addEventListener('click', () => {
        // Deactivate all siblings
        label
          .closest('[data-quiz-input-type="radio"]')
          .querySelectorAll('.quiz-option')
          .forEach((sib) => {
            sib.classList.remove('border-teal-500', 'bg-teal-50');
            sib.classList.add('border-gray-200', 'bg-white');
            const indicator = sib.querySelector('span:last-child');
            if (indicator) {
              indicator.classList.remove('border-teal-500', 'bg-teal-500');
              indicator.classList.add('border-gray-300');
              const dot = indicator.querySelector('span');
              if (dot) dot.classList.add('hidden');
            }
          });

        // Activate clicked option
        label.classList.add('border-teal-500', 'bg-teal-50');
        label.classList.remove('border-gray-200', 'bg-white');
        const indicator = label.querySelector('span:last-child');
        if (indicator) {
          indicator.classList.add('border-teal-500', 'bg-teal-500');
          indicator.classList.remove('border-gray-300');
          const dot = indicator.querySelector('span');
          if (dot) dot.classList.remove('hidden');
        }
      });
    });

  // Checkbox options
  document
    .querySelectorAll('[data-quiz-input-type="checkbox"] .quiz-option')
    .forEach((label) => {
      const input = label.querySelector('input[type="checkbox"]');
      if (!input) return;

      label.addEventListener('click', () => {
        // Toggle after the browser has updated checked state (next tick)
        requestAnimationFrame(() => {
          const checked = input.checked;
          label.classList.toggle('border-teal-500', checked);
          label.classList.toggle('bg-teal-50', checked);
          label.classList.toggle('border-gray-200', !checked);
          label.classList.toggle('bg-white', !checked);

          const indicator = label.querySelector('span:first-of-type');
          if (indicator) {
            indicator.classList.toggle('border-teal-500', checked);
            indicator.classList.toggle('bg-teal-500', checked);
            indicator.classList.toggle('border-gray-300', !checked);
            const tick = indicator.querySelector('svg');
            if (tick) tick.classList.toggle('hidden', !checked);
          }
        });
      });
    });
}

// ─── Button / link event tracking ────────────────────────────────────────────

function initEventTracking() {
  document.addEventListener('click', (e) => {
    const el = e.target.closest('[data-quiz-event]');
    if (!el) return;

    const eventName = el.dataset.quizEvent;
    const productId = el.dataset.productId;
    const consent = el.dataset.consent;

    switch (eventName) {
      case 'quiz_started':
        pushEvent('quiz_started');
        break;
      case 'quiz_lead_submitted':
        pushEvent('quiz_lead_submitted', { consent: consent !== 'false' });
        break;
      case 'quiz_product_clicked':
        pushEvent('quiz_product_clicked', { product_id: productId });
        break;
    }
  });

  // Track question form submissions — delegated so it survives HTMX swaps
  document.addEventListener('submit', (e) => {
    const form = e.target.closest(
      'form[data-quiz-event="quiz_question_answered"]',
    );
    if (!form || document.activeElement?.name === 'back') return;

    const questionId = form.dataset.questionId;
    const inputType = form.querySelector('[data-quiz-input-type]')?.dataset
      .quizInputType;
    let answer;

    if (inputType === 'radio') {
      answer = form.querySelector('input[type="radio"]:checked')?.value;
    } else if (inputType === 'checkbox') {
      answer = Array.from(
        form.querySelectorAll('input[type="checkbox"]:checked'),
      )
        .map((cb) => cb.value)
        .join(', ');
    } else {
      answer = form.querySelector('input[type="text"]')?.value;
    }

    pushEvent('quiz_question_answered', { question_id: questionId, answer });
  });
}

// ─── Recommendation card hover colours ───────────────────────────────────────

function initRecHover() {
  document.querySelectorAll('[data-quiz-rec]').forEach((el) => {
    const bg = el.dataset.quizRec;
    if (!bg) return;
    el.addEventListener('mouseenter', () => {
      el.style.backgroundColor = bg;
    });
    el.addEventListener('mouseleave', () => {
      el.style.backgroundColor = '';
    });
  });
}

// ─── Results tracking ────────────────────────────────────────────────────────

function initResultsTracking() {
  if (!document.querySelector('[data-quiz-event="quiz_product_clicked"]'))
    return;
  pushEvent('quiz_completed', { result_id: 'quiz_results' });
}

// ─── HTMX lifecycle ───────────────────────────────────────────────────────────

function initHtmxQuiz() {
  // htmx:load fires on the newly inserted element while it IS in the DOM,
  // so the event bubbles correctly to document (unlike htmx:afterSettle which
  // fires on the detached trigger element after an outerHTML swap).
  document.addEventListener('htmx:load', (e) => {
    if (e.target.id !== 'quiz-page-content') return;

    // Animate preserved progress bar to new value
    const progressEl = e.target.querySelector('[data-quiz-progress]');
    if (progressEl) {
      const percent = parseInt(progressEl.dataset.quizProgress, 10);
      const fill = document.getElementById('quiz-progress-fill');
      const label = document.getElementById('quiz-progress-label');
      if (fill) {
        fill.style.width = percent + '%';
        fill.setAttribute('aria-valuenow', percent);
      }
      if (label) label.textContent = percent + '%';
    }

    initOptionHighlight();
    initThinkingRedirect();
    initRecHover();
    initResultsTracking();
    initTabs();
    initSelectDropdowns();
    initQuizResultsPurchase();
    initQuizResultsBottomBar();
  });
}

// ─── Quiz results bottom bar ──────────────────────────────────────────────────

function initQuizResultsBottomBar() {
  const addBothBtn = document.getElementById(
    'add-both-to-cart',
  ) as HTMLButtonElement | null;
  const chooseBtn = document.getElementById('choose-separately');
  if (!addBothBtn && !chooseBtn) return;

  addBothBtn?.addEventListener('click', async () => {
    addBothBtn.disabled = true;

    const containers = Array.from(
      document.querySelectorAll<HTMLElement>('[data-purchase-suffix]'),
    );

    for (let i = 0; i < containers.length - 1; i++) {
      const suffix = containers[i].dataset.purchaseSuffix ?? '';
      const varId =
        (
          document.getElementById(
            'input-varId' + suffix,
          ) as HTMLInputElement | null
        )?.value ?? '';
      const qty =
        containers[i].querySelector<HTMLInputElement>(
          `input[name="quantity${suffix}"]:checked`,
        )?.value ?? '1';
      const sub =
        (
          document.getElementById(
            'subscription-frequency' + suffix,
          ) as HTMLInputElement | null
        )?.value ?? '';
      if (!varId) continue;
      await fetch('/api/cart/add', {
        method: 'POST',
        body: new URLSearchParams({ varId, quantity: qty, subscription: sub }),
      });
    }

    addBothBtn.disabled = false;

    const last = containers[containers.length - 1];
    if (last) {
      const lastSuffix = last.dataset.purchaseSuffix ?? '';
      (
        document.getElementById(
          'add-to-cart-button' + lastSuffix,
        ) as HTMLElement | null
      )?.click();
    }
  });

  chooseBtn?.addEventListener('click', () => {
    document
      .querySelectorAll<HTMLElement>('[id^="add-to-cart-button"]')
      .forEach((el) => el.classList.remove('hidden'));
    document.getElementById('quiz-bottom-bar')?.classList.add('hidden');
  });
}

// ─── Quiz results purchase init ───────────────────────────────────────────────

function initQuizResultsPurchase() {
  if (window.location.pathname !== '/quiz/results') return;

  document
    .querySelectorAll<HTMLElement>('[data-purchase-suffix]')
    .forEach((container) => {
      const suffix = container.dataset.purchaseSuffix ?? '';
      const totalEl =
        container.querySelector<HTMLElement>('[data-price-total]');
      const priceJson: VariantPrices = totalEl?.dataset.priceJson
        ? JSON.parse(totalEl.dataset.priceJson)
        : {};
      const originalPriceJson: OriginalPrices = totalEl?.dataset
        .originalPriceJson
        ? JSON.parse(totalEl.dataset.originalPriceJson)
        : {};
      initProductPurchaseInstance(suffix, priceJson, originalPriceJson);
    });
}

// ─── Prevent duplicate quiz form submissions ─────────────────────────────────
// Block re-submitting the same answer — catches spam clicks and repeated
// submits after a validation error. Fingerprint stored on the form element
// resets automatically when HTMX swaps in a new question (new form DOM).

function getAnswerFingerprint(form: HTMLFormElement): string {
  const data = new FormData(form);
  return (data.getAll('answers') as string[])
    .map((v) => v.trim())
    .filter(Boolean)
    .sort()
    .join('|');
}

// Stored outside the form element so they survive HTMX outerHTML swaps
let _lastSubmittedFingerprint: string | undefined;
let _lastSubmittedQuestionId: string | undefined;

function initQuizSubmitGuard() {
  // Capture phase so we run before HTMX's submit listener
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target as HTMLFormElement;
      if (form.id !== 'quiz-form') return;
      // Always allow Back button submissions
      if (
        (document.activeElement as HTMLElement)?.getAttribute('name') === 'back'
      )
        return;

      const fingerprint = getAnswerFingerprint(form);
      const questionId = form.dataset.questionId;

      // New question — reset so the first submit always goes through
      if (questionId !== _lastSubmittedQuestionId) {
        _lastSubmittedFingerprint = undefined;
        _lastSubmittedQuestionId = questionId;
      }

      if (_lastSubmittedFingerprint === fingerprint) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      _lastSubmittedFingerprint = fingerprint;
    },
    true,
  );
}

// ─── Prevent Enter from submitting on text inputs ────────────────────────────
// On mobile, the keyboard's action button fires Enter which submits the form.
// Instead, just blur the input to hide the keyboard and keep the value.

document.addEventListener('keydown', (e) => {
  if (
    e.key === 'Enter' &&
    (e.target as HTMLElement).matches('input[type="text"]')
  ) {
    e.preventDefault();
    (e.target as HTMLInputElement).blur();
  }
});

// ─── Init ─────────────────────────────────────────────────────────────────────
// module scripts are implicitly deferred — DOM is ready when this runs

initThinkingRedirect();
initOptionHighlight();
initEventTracking();
initResultsTracking();
initQuizSubmitGuard();
initRecHover();
initHtmxQuiz();
initTabs();
initSelectDropdowns();
initQuizResultsPurchase();
initQuizResultsBottomBar();
